import React, { FC, ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { IReportConfig } from '../interfaces/report-config'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../../stores/app'
import * as dotProp from 'dot-prop'
import { Cell, Column, ColumnHeaderProps, ElementOrFunc, Plugins, Table } from 'fixed-data-table-2'
import { AutoSizer } from 'react-virtualized'
import { resolveColumnTableOrder } from '../constants/column-table-order'
import { ColumnConfiguration } from '../types'
import { isFunction } from '@pushly/aqe/lib/utils'
import './report-table.scss'

import { SortHeaderCell } from './sort-header-cell'
import { COLUMN_CONFIGS_BY_TYPE_MAP } from '../constants/base'
import { PlatformLevel } from '../enums/platform-level'
import { DomainDto } from '../../../dtos/domain'
import { GetAccountResponse } from '@pushly/models/lib/structs'

const SORT_TYPES = {
    ASC: 'ASC',
    DESC: 'DESC',
}

export type ReportTableProps = {
    loading: boolean
    config: IReportConfig
    data: any[]
    columns: string[]
    hiddenColumns?: string[]
    level: PlatformLevel
    domain: DomainDto
    org?: GetAccountResponse
}

const FIXED_COLUMNS = ['send_date']

export const ReportTable: FC<ReportTableProps> = ({
    config,
    data = [],
    columns,
    hiddenColumns,
    level,
    domain,
    org,
}) => {
    const appState = useService(AppState)

    // stored data state
    const [sortedData, setSortedData] = useState<any[]>([])
    const [sortBy, setSortBy] = useState('send_date')
    const [sortDirection, setSortDirection] = useState(SORT_TYPES.DESC)

    // column state
    const [columnOrder, setColumnOrder] = useState<string[]>([])
    const [columnWidths, setColumnWidths] = useState<{ [p: string]: number }>({})

    // region sorting definitions

    const sort = useCallback(
        (by?: string, direction?: string) => {
            const passedSortBy = by ?? sortBy
            let correctedSortBy = passedSortBy

            if (passedSortBy === 'display') {
                switch (config.type) {
                    case 'DELIVERY':
                        correctedSortBy = 'title'
                        break
                    case 'PUB_NETWORK':
                        correctedSortBy = 'report_date'
                        break
                    default:
                        correctedSortBy = 'name'
                        break
                }
            }

            // The Uniques report's Date column keys on report_month at month grain but
            // report_date at day grain; remap so client-side date sort resolves the field
            // that actually exists on the day-grain rows.
            if (passedSortBy === 'report_month' && config.dateIncrement === 'DAY') {
                correctedSortBy = 'report_date'
            }

            const determinedSortDirection = direction ?? sortDirection

            window.__SW_REPORT_TABLE__ = { sortBy, sortDirection }
            setSortBy(correctedSortBy)
            setSortDirection(determinedSortDirection)
            setSortedData(sortList(correctedSortBy, direction ?? sortDirection))
        },
        [sortBy, sortDirection, config],
    )

    const sortList = useCallback(
        (by: string, direction: string, tableConfig?: ColumnConfiguration): any[] => {
            const reportData: any[] = data || []

            let sortFunc = (a: any, b: any) => {
                let aValue: any = String(dotProp.get(a, by)).toLowerCase()
                let bValue: any = String(dotProp.get(b, by)).toLowerCase()

                const aIsNumeric = !isNaN(aValue as any)
                const bIsNumeric = !isNaN(bValue as any)
                if (aIsNumeric && bIsNumeric) {
                    aValue = parseFloat(aValue)
                    bValue = parseFloat(bValue)
                }

                if (aValue === '--' || aValue === 'null') {
                    aValue = -1
                }
                if (bValue === '--' || bValue === 'null') {
                    bValue = -1
                }

                if (tableConfig && tableConfig.sortType !== 'number') {
                    aValue = aValue.toString()
                    bValue = bValue.toString()
                }

                return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
            }

            if (tableConfig && tableConfig.sort) {
                sortFunc = tableConfig.sort
            }

            let rDataSorted = reportData.sort(sortFunc)

            if (direction === SORT_TYPES.DESC) {
                rDataSorted = rDataSorted.reverse()
            }

            return rDataSorted
        },
        [config, data],
    )

    // endregion sorting definitions
    // region Effects

    useEffect(() => {
        const handleForcedSort = (event: CustomEvent) => {
            sort(event.detail.sortBy, event.detail.sortDirection)
        }

        window.addEventListener('sw-force-report-sort', handleForcedSort)

        return () => window.removeEventListener('sw-force-report-sort', handleForcedSort)
    }, [])

    useEffect(() => {
        const columnConfigurations = COLUMN_CONFIGS_BY_TYPE_MAP[config.type!]
        if (columnConfigurations) {
            const cols = Object.keys(columnConfigurations)
            const widths = columnWidths
            cols.forEach((col) => {
                const configuration = columnConfigurations[col]
                if (Array.isArray(configuration)) {
                    configuration.forEach((conf) => {
                        widths[conf.columnKey] = conf.width
                    })
                } else {
                    widths[configuration.columnKey] = configuration.width
                }
            })

            setColumnWidths(widths)
        }
    }, [config, columns])

    useEffect(() => {
        setColumnOrder(resolveColumnTableOrder(config.type, config.level, config.breakdown))
    }, [columns, hiddenColumns, config])

    // endregion Effects

    // region Column Rendering

    const columnsToDisplay = useMemo(() => {
        const _columns = (columns || [])
            .filter((col) => !!col && !hiddenColumns?.includes(col))
            .map((col) => col.toUpperCase())

        if (config.dateIncrement !== 'LIFETIME') {
            _columns.push('DATE')
        }

        return _columns
    }, [columns])

    const onColumnResizeEnd = (newColumnWidth: number, columnKey: string) => {
        setColumnWidths((widths) => ({
            ...widths,
            [columnKey]: newColumnWidth,
        }))
    }

    const dynamicColumnRenderer = useCallback(
        (configuration: ColumnConfiguration) => {
            let header: JSX.Element | null = null
            let footer: ElementOrFunc<ColumnHeaderProps> | undefined

            if (!configuration.hideFooter) {
                footer = (props: ColumnHeaderProps) => {
                    let cell: ReactElement | undefined

                    if (configuration.footer) {
                        if (isFunction(configuration.footer)) {
                            cell = configuration.footer(data, props)
                        } else {
                            cell = configuration.footer
                        }
                    }

                    if (!cell) {
                        cell = (
                            <Cell {...props}>
                                <div className="footer-primary empty" />
                                <div className="footer-secondary" />
                            </Cell>
                        )
                    }

                    return cell
                }
            }

            let title: ReactNode | undefined
            if (configuration.title) {
                if (isFunction(configuration.title)) {
                    title = configuration.title(config, configuration)
                } else {
                    title = configuration.title
                }
            }

            header = configuration.sortable ? (
                <SortHeaderCell
                    key={configuration.columnKey || configuration.key}
                    title={title}
                    onSortChange={sort}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    width={configuration.width}
                />
            ) : (
                <Cell className={configuration.className}>{title}</Cell>
            )

            return (
                <Column
                    {...configuration}
                    cellClassName={`${configuration.className} ${
                        FIXED_COLUMNS.indexOf(configuration.columnKey) !== -1 ? 'fixed-column' : ''
                    }`}
                    key={configuration.columnKey || configuration.key}
                    header={<Plugins.ResizeCell onColumnResizeEnd={onColumnResizeEnd}>{header}</Plugins.ResizeCell>}
                    // fixed={FIXED_COLUMNS.indexOf(configuration.columnKey) !== -1}
                    footer={footer}
                    allowCellsRecycling={true}
                    width={columnWidths[configuration.columnKey] || configuration.width}
                    cell={({ rowIndex, ...props }) => {
                        const record = data[rowIndex]

                        let content
                        if (!!configuration.render) {
                            content =
                                level === PlatformLevel.ORG
                                    ? configuration.render(record, data, config, appState, PlatformLevel.ORG, org!)
                                    : configuration.render(record, data, config, appState, PlatformLevel.DOMAIN, domain)
                        } else {
                            content = dotProp.get(record, configuration.columnKey)
                        }

                        return (
                            <Cell {...props} className={configuration.className}>
                                {content}
                            </Cell>
                        )
                    }}
                />
            )
        },
        [config, data, columnOrder, sortBy, sortDirection, hiddenColumns, columnsToDisplay, columnWidths],
    )

    const columnNodes: any[] = []
    columnOrder.forEach((column) => {
        if (columnsToDisplay.indexOf(column) !== -1) {
            if (column in COLUMN_CONFIGS_BY_TYPE_MAP[config.type!]) {
                const colConfig = COLUMN_CONFIGS_BY_TYPE_MAP[config.type!][column]

                if (Array.isArray(colConfig)) {
                    colConfig.forEach((conf) => {
                        columnNodes.push(dynamicColumnRenderer(conf))
                    })
                } else {
                    columnNodes.push(dynamicColumnRenderer(colConfig))
                }
            }
        }
    })

    // endregion Column Rendering

    return (
        <div className="report-table-wrapper">
            <AutoSizer>
                {({ height, width }) => {
                    return (
                        <Table
                            className="report-table"
                            width={width}
                            height={138 + data.length * 60 < height ? 138 + data.length * 60 : height}
                            rowsCount={data.length}
                            rowHeight={60}
                            headerHeight={60}
                            footerHeight={60}
                        >
                            {columnNodes}
                        </Table>
                    )
                }}
            </AutoSizer>
        </div>
    )
}
